import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import {
  Wrench, AlertTriangle, RefreshCw, CalendarClock, Circle,
} from 'lucide-react';
import type { SiteEvent } from '../../../redux/api/eventsApi';
import type { PortfolioSite } from '../../../redux/api/portfolioApi';
import { setCurrentSite } from '../../../redux/slices/siteSlice';
import { useAppDispatch } from '../../../redux/hooks';
import { useTheme } from '../../../contexts';
import { deriveEventCategory, type EventCategory } from '../../../constants/eventCategories';

type SortMode = 'recent' | 'safety';

const SORT_LABELS: Record<SortMode, string> = {
  recent: 'Recent',
  safety: 'Safety',
};

const CATEGORY_GRADIENTS: Record<EventCategory, { from: string; to: string }> = {
  preventative_maintenance: { from: '#3B82F6', to: '#93C5FD' },
  corrective_maintenance: { from: '#EF4444', to: '#FCA5A5' },
  asset_update: { from: '#8B5CF6', to: '#C4B5FD' },
  upcoming: { from: '#F59E0B', to: '#FCD34D' },
  other: { from: '#6B7280', to: '#D1D5DB' },
};

const CATEGORY_ICONS: Record<EventCategory, React.ComponentType<{ className?: string }>> = {
  preventative_maintenance: Wrench,
  corrective_maintenance: AlertTriangle,
  asset_update: RefreshCw,
  upcoming: CalendarClock,
  other: Circle,
};

interface SiteWithEvents {
  siteId: string;
  siteName: string;
  site?: PortfolioSite;
  events: Array<SiteEvent & { category: EventCategory }>;
  sortKey: number; // timestamp of the qualifying event
}

interface DashboardTimelineModuleProps {
  events: SiteEvent[];
  sites: PortfolioSite[];
  siteNameMap: Record<string, string>;
  onEventClick?: (eventId: string) => void;
}

const ROW_HEIGHT = 44;
const LABEL_WIDTH = 160;
const TOP_SITES = 3;

const DashboardTimelineModule: React.FC<DashboardTimelineModuleProps> = ({
  events,
  sites,
  siteNameMap,
  onEventClick,
}) => {
  const dispatch = useAppDispatch();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  // Categorize all events
  const categorizedEvents = useMemo(() => {
    return events.map((e) => ({
      ...e,
      category: (e.event_category || deriveEventCategory(e.event_type, e.occurred_at)) as EventCategory,
    }));
  }, [events]);

  // Group by site, pick top 3 by sort mode
  const topSites: SiteWithEvents[] = useMemo(() => {
    const bySite = new Map<string, Array<SiteEvent & { category: EventCategory }>>();
    categorizedEvents.forEach((e) => {
      if (!bySite.has(e.site_id)) bySite.set(e.site_id, []);
      bySite.get(e.site_id)!.push(e);
    });

    const ranked: SiteWithEvents[] = [];
    bySite.forEach((siteEvents, siteId) => {
      let sortKey = 0;
      if (sortMode === 'recent') {
        // Most recent event
        sortKey = Math.max(...siteEvents.map((e) => new Date(e.occurred_at).getTime()));
      } else {
        // Most recent safety event (incident or open critical)
        const safetyEvents = siteEvents.filter(
          (e) => e.event_type === 'incident' || (e.status === 'open' && e.severity === 'critical')
        );
        if (safetyEvents.length > 0) {
          sortKey = Math.max(...safetyEvents.map((e) => new Date(e.occurred_at).getTime()));
        }
      }

      if (sortMode === 'safety' && sortKey === 0) return; // skip sites with no safety events

      ranked.push({
        siteId,
        siteName: siteNameMap[siteId] || siteId,
        site: sites.find((s) => (s.site_id || s.id) === siteId),
        events: siteEvents.sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()),
        sortKey,
      });
    });

    ranked.sort((a, b) => b.sortKey - a.sortKey);
    return ranked.slice(0, TOP_SITES);
  }, [categorizedEvents, sortMode, siteNameMap, sites]);

  // Global time range from visible events
  const dateRange: [Date, Date] = useMemo(() => {
    const allDates = topSites.flatMap((s) => s.events.map((e) => new Date(e.occurred_at).getTime()));
    if (allDates.length === 0) {
      const now = Date.now();
      return [new Date(now - 365 * 86400000), new Date(now)];
    }
    const min = Math.min(...allDates);
    const max = Math.max(...allDates);
    const range = max - min || 86400000;
    return [new Date(min - range * 0.05), new Date(max + range * 0.05)];
  }, [topSites]);

  const timeScale = useMemo(
    () => d3.scaleTime().domain(dateRange).range([LABEL_WIDTH, width - 12]),
    [dateRange, width]
  );

  // Axis ticks
  const axisTicks = useMemo(() => {
    const ticks = timeScale.ticks(5);
    return ticks.map((t) => ({
      x: timeScale(t),
      label: d3.timeFormat('%b %Y')(t),
    }));
  }, [timeScale]);

  const handleSiteClick = useCallback((sw: SiteWithEvents) => {
    if (sw.site) {
      dispatch(setCurrentSite(sw.site));
    }
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'interactivetimeline' } }));
  }, [dispatch]);

  const svgHeight = topSites.length * ROW_HEIGHT + 24; // +24 for axis

  if (events.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm py-8" style={{ color: 'var(--color-text-muted)' }}>
        No events to display
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full min-w-0">
      {/* Sort toggle */}
      <div className="flex gap-1 mb-3">
        {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => setSortMode(mode)}
            className="px-2.5 py-1 rounded text-xs font-medium transition-all"
            style={{
              backgroundColor: sortMode === mode ? 'rgba(23, 196, 128, 0.15)' : 'transparent',
              color: sortMode === mode ? '#17c480' : 'var(--color-text-muted)',
            }}
          >
            {SORT_LABELS[mode]}
          </button>
        ))}
      </div>

      {topSites.length === 0 ? (
        <div className="text-sm py-6 text-center" style={{ color: 'var(--color-text-muted)' }}>
          No {sortMode === 'safety' ? 'safety' : ''} events found
        </div>
      ) : (
        <svg width={width} height={svgHeight} className="block">
          {/* Label column separator */}
          <line
            x1={LABEL_WIDTH} y1={0}
            x2={LABEL_WIDTH} y2={svgHeight - 24}
            stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}
          />

          {/* Axis tick lines */}
          {axisTicks.map((tick, i) => (
            <g key={i}>
              <line
                x1={tick.x} y1={0}
                x2={tick.x} y2={svgHeight - 24}
                stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}
              />
              <text
                x={tick.x} y={svgHeight - 6}
                textAnchor="middle"
                fill={isDark ? '#6B7280' : '#9CA3AF'}
                fontSize={9}
              >
                {tick.label}
              </text>
            </g>
          ))}

          {/* Site rows */}
          {topSites.map((sw, rowIdx) => {
            const y = rowIdx * ROW_HEIGHT;
            return (
              <g key={sw.siteId} transform={`translate(0, ${y})`}>
                {/* Row hover bg */}
                <rect
                  x={0} y={0}
                  width={width} height={ROW_HEIGHT}
                  fill={isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'}
                  className="cursor-pointer"
                  rx={4}
                  onClick={() => handleSiteClick(sw)}
                >
                  <animate attributeName="fill" from={isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'} to={isDark ? 'rgba(23,196,128,0.08)' : 'rgba(23,196,128,0.06)'} dur="0.15s" begin="mouseover" fill="freeze" />
                  <animate attributeName="fill" from={isDark ? 'rgba(23,196,128,0.08)' : 'rgba(23,196,128,0.06)'} to={isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'} dur="0.15s" begin="mouseout" fill="freeze" />
                </rect>

                {/* Site label */}
                <text
                  x={12} y={ROW_HEIGHT / 2}
                  dominantBaseline="middle"
                  fill={isDark ? '#E5E7EB' : '#374151'}
                  fontSize={12}
                  fontWeight={600}
                  className="cursor-pointer"
                  onClick={() => handleSiteClick(sw)}
                >
                  {sw.siteName.length > 18 ? sw.siteName.substring(0, 18) + '...' : sw.siteName}
                </text>

                {/* Drill arrow */}
                <text
                  x={LABEL_WIDTH - 16} y={ROW_HEIGHT / 2}
                  dominantBaseline="middle"
                  fill={isDark ? '#6B7280' : '#9CA3AF'}
                  fontSize={10}
                  className="cursor-pointer"
                  onClick={() => handleSiteClick(sw)}
                >
                  ▸
                </text>

                {/* Event dots */}
                {sw.events.map((event) => {
                  const x = timeScale(new Date(event.occurred_at));
                  if (x < LABEL_WIDTH || x > width - 8) return null;
                  const jitter = ((event.id.charCodeAt(0) % 5) - 2) * 3;
                  const grad = CATEGORY_GRADIENTS[event.category];
                  return (
                    <circle
                      key={event.id}
                      cx={x}
                      cy={ROW_HEIGHT / 2 + jitter}
                      r={4}
                      fill={grad.from}
                      opacity={0.8}
                      className="cursor-pointer hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick?.(event.id);
                      }}
                    >
                      <title>{`${event.title} (${event.severity} · ${event.event_type})`}</title>
                    </circle>
                  );
                })}

                {/* Event count */}
                <text
                  x={width - 8} y={ROW_HEIGHT / 2}
                  dominantBaseline="middle"
                  textAnchor="end"
                  fill={isDark ? '#9CA3AF' : '#6B7280'}
                  fontSize={10}
                >
                  {sw.events.length}
                </text>

                {/* Row separator */}
                <line
                  x1={0} y1={ROW_HEIGHT}
                  x2={width} y2={ROW_HEIGHT}
                  stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
                />
              </g>
            );
          })}
        </svg>
      )}

      {/* Category legend */}
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {Object.entries(CATEGORY_GRADIENTS).map(([cat, grad]) => {
          const Icon = CATEGORY_ICONS[cat as EventCategory];
          return (
            <span key={cat} className="flex items-center gap-1 text-[10px]" style={{ color: grad.from }}>
              <Icon className="w-3 h-3" />
              {cat.replace(/_/g, ' ')}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardTimelineModule;
